/**
 * Fail-closed company_settings access. Always filter by organization_id.
 * Never use unscoped limit(1) — leftover impersonation would load Fatiha into T&B.
 */

import { supabase } from '../../lib/supabase.js';
import { getAppMode, isSaasPlatform } from '../appMode.js';
import { getActiveWorkspaceOrgId } from './activeWorkspaceOrgId.js';
import { LEGACY_TB_COMPANY_SETTINGS_ID } from './companySettingsDraft.js';
import { ensureTbSiteWorkspaceOrg, fetchTbSiteOrganization, setWorkspaceOrg } from './saasOrg.js';

/** Prefer the caller’s workspace. T&B site only fills in when no org was passed. */
export function pickCompanySettingsOrgId(input: {
  explicit?: string | null;
  isTbSite: boolean;
  tbOrgId?: string | null;
  activeOrgId?: string | null;
}): string | null {
  const explicit = input.explicit?.trim() || null;
  if (explicit) return explicit;
  if (input.isTbSite) return input.tbOrgId?.trim() || null;
  return input.activeOrgId?.trim() || null;
}

export async function resolveCompanySettingsOrgId(explicit?: string | null): Promise<string | null> {
  if (getAppMode() === 'tb-site' && !explicit?.trim()) {
    const tb = await fetchTbSiteOrganization();
    if (tb?.id) {
      void ensureTbSiteWorkspaceOrg();
      return tb.id;
    }
    return null;
  }
  return pickCompanySettingsOrgId({
    explicit,
    isTbSite: getAppMode() === 'tb-site',
    tbOrgId: null,
    activeOrgId: getActiveWorkspaceOrgId(),
  });
}

const PIN_WORKSPACE_TIMEOUT_MS = 3000;

async function pinWorkspace(orgId: string): Promise<void> {
  try {
    await Promise.race([
      (async () => {
        await setWorkspaceOrg(orgId);
        const { data: resolved } = await supabase.rpc('request_org_id');
        if (resolved && resolved !== orgId) {
          await setWorkspaceOrg(orgId);
        }
      })(),
      new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('pin_timeout')), PIN_WORKSPACE_TIMEOUT_MS);
      }),
    ]);
  } catch {
    /* membership RPC still authorizes the org-scoped read */
  }
}

function rowBelongsToOrg(
  row: { organization_id?: string | null; id?: string | null } | null,
  orgId: string,
): boolean {
  if (!row) return false;
  if (row.organization_id && row.organization_id !== orgId) return false;
  if (
    isSaasPlatform() &&
    row.id === LEGACY_TB_COMPANY_SETTINGS_ID &&
    row.organization_id &&
    row.organization_id !== orgId
  ) {
    return false;
  }
  return true;
}

export async function fetchOrgCompanySettingsRow<T = Record<string, unknown>>(
  orgId: string | null | undefined,
  columns = '*',
): Promise<T | null> {
  const id = await resolveCompanySettingsOrgId(orgId);
  if (!id) return null;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  // saas_get_org_company_settings is authenticated-only and returns null when
  // auth.uid() is missing. Calling it as anon (public T&B pages, crawlers)
  // is a permission error on every page view. Signed-out reads use RLS:
  // request_org_id() is the default tenant, so the table query below still
  // returns that org's public settings.
  if (session?.user) {
    await pinWorkspace(id);

    const { data: rpcData, error: rpcError } = await supabase.rpc('saas_get_org_company_settings', {
      p_org_id: id,
    });
    if (!rpcError && rpcData && typeof rpcData === 'object') {
      const rpcRow = rpcData as T & { organization_id?: string; id?: string };
      if (rowBelongsToOrg(rpcRow, id)) return rpcRow as T;
    }
  }

  const { data, error } = await supabase
    .from('company_settings')
    .select(columns)
    .eq('organization_id', id)
    .maybeSingle();
  if (error || !data || typeof data !== 'object') return null;
  const row = data as unknown as T & { organization_id?: string; id?: string };
  if (!rowBelongsToOrg(row, id)) return null;
  return row as T;
}

export async function updateOrgCompanySettings(
  orgId: string | null | undefined,
  payload: Record<string, unknown>,
  options?: { rowId?: string | null },
): Promise<{ data: Record<string, unknown> | null; error: string | null }> {
  const id = await resolveCompanySettingsOrgId(orgId);
  if (!id) return { data: null, error: 'Workspace organization is required to save company settings' };
  await pinWorkspace(id);

  if (isSaasPlatform() && options?.rowId === LEGACY_TB_COMPANY_SETTINGS_ID) {
    const { data: tbOrg } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', 'toolsandbubbles')
      .maybeSingle();
    if (tbOrg?.id && tbOrg.id !== id) {
      return { data: null, error: 'Refusing to write Tools & Bubbles settings from another workspace' };
    }
  }

  const { data: rpcData, error: rpcError } = await supabase.rpc('saas_upsert_org_company_settings', {
    p_org_id: id,
    p_patch: payload,
  });
  if (!rpcError && rpcData && typeof rpcData === 'object') {
    const rpcRow = rpcData as Record<string, unknown> & { organization_id?: string; id?: string };
    if (rowBelongsToOrg(rpcRow, id)) return { data: rpcRow, error: null };
  }
  if (rpcError && !/could not find the function/i.test(rpcError.message)) {
    return { data: null, error: rpcError.message };
  }

  let query = supabase.from('company_settings').update(payload).eq('organization_id', id);
  if (options?.rowId) {
    query = query.eq('id', options.rowId);
  }
  const { data, error } = await query.select('*').maybeSingle();
  if (error) return { data: null, error: error.message };
  if (!data) {
    return insertOrgCompanySettings(id, payload);
  }
  if (!rowBelongsToOrg(data as { organization_id?: string; id?: string }, id)) {
    return { data: null, error: 'Company settings row does not belong to this workspace' };
  }
  return { data: data as Record<string, unknown>, error: null };
}

export async function insertOrgCompanySettings(
  orgId: string | null | undefined,
  payload: Record<string, unknown>,
): Promise<{ data: Record<string, unknown> | null; error: string | null }> {
  const id = await resolveCompanySettingsOrgId(orgId);
  if (!id) return { data: null, error: 'Workspace organization is required to save company settings' };
  await pinWorkspace(id);
  const { data, error } = await supabase
    .from('company_settings')
    .insert({ ...payload, organization_id: id })
    .select('*')
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data: (data as Record<string, unknown> | null) ?? null, error: null };
}
